import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import StatusIndicator, {
  type StatusIndicatorProps,
} from '@cloudscape-design/components/status-indicator';
import { InvoiceStatus, type InvoiceStatus as InvoiceStatusT } from '@etiquetachef/types';

/**
 * Cor/ícone do `StatusIndicator` por status de fatura. `Record` sobre o enum:
 * status novo em `InvoiceStatus` reprova o typecheck aqui em vez de renderizar
 * sem cor.
 */
const STATUS_TYPE: Record<InvoiceStatusT, StatusIndicatorProps.Type> = {
  [InvoiceStatus.PENDING]: 'pending',
  [InvoiceStatus.PAID]: 'success',
  [InvoiceStatus.OVERDUE]: 'warning',
  [InvoiceStatus.CANCELLED]: 'stopped',
  [InvoiceStatus.REFUNDED]: 'info',
  [InvoiceStatus.VOID]: 'stopped',
};

type Props = { status: InvoiceStatusT; label?: string };

/**
 * Badge de status de fatura do admin (Cloudscape `StatusIndicator`).
 *
 * Origem: sem issue — commit 627b442cc, 2026-06-09 18:50 +0200 (detalhe do
 * tenant em Cloudscape; substituiu o `InvoiceStatusBadge` Tailwind do
 * ui-shared, que já não existe — o admin bane `@etiquetachef/ui-shared`).
 *
 * Contrato: `label` sobrepõe o texto; sem ele, `billing.invoiceStatus.<status>`
 * (i18n, os 5 locales). Só apresentação — não lê Firestore.
 *
 * Usado por: `components/InvoiceTable.tsx`, `routes/InvoiceDetailPage.tsx`,
 * `routes/PendingPage.tsx`, `features/finance/TenantFinanceTab.tsx` (admin web).
 */
export function InvoiceStatusBadge({ status, label }: Props): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <StatusIndicator type={STATUS_TYPE[status]}>
      {label ?? t(`billing.invoiceStatus.${status}`)}
    </StatusIndicator>
  );
}
